import { Encoding } from '@mode-vegas/vegas';
import * as angular from 'angular';
import { Observable, combineLatest as staticCombineLatest } from 'rxjs';
import { combineLatest, debounceTime, filter, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';

import { EmbedActionType, EmbedActions, EmbedReportState, ReportStore } from '@mode/reports/sandbox/contract';
import { EmbedChartState } from '@mode/legacy/types-app-state';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { isPresent, shareReplayDefault } from '@mode/shared/util-js';
import { MixableBaseController } from '../../../common/component';
import { VizTypes } from '../../../common/types';
import { EmbedChartSelectors, EmbedReportSelectors } from '../domain/embed-report.selectors';

class ModeChartController extends MixableBaseController {
  static $inject = ['$scope', '$window', '$element', 'RxStoreService', 'FeatureFlagsFacade'];

  id: string;
  token: string;
  vizType: VizTypes;
  isRendering: boolean;
  virtualized: boolean;
  renderStartTime: number;
  disableDomCache: boolean;
  vizRendered: boolean;

  dataset$: Observable<EmbedReportState.EmbedDataset>;
  chartType$: Observable<ChartTypes.ChartType>;
  encoding$: Observable<Encoding>;
  format$: Observable<ChartTypes.ChartFormat>;
  v1View$: Observable<ChartTypes.ChartView>;
  vegasRenderInfo$: Observable<ChartTypes.VegasRenderInfo>;
  state$: Observable<EmbedChartState>;
  chartReady$: Observable<boolean>;

  viewer$: Observable<string>;
  isEmbedded$: Observable<boolean>;
  isV1$: Observable<boolean>;
  isInViewport$: Observable<boolean>;
  isLegacyChart$: Observable<boolean>;
  isMetricChart$: Observable<boolean>;
  isVegasChart$: Observable<boolean>;
  isVegasBigValue$: Observable<boolean>;
  isVegasTable$: Observable<boolean>;
  isPivotTable$: Observable<boolean>;
  isBigNumber$: Observable<boolean>;
  chartTitle$: Observable<string>;
  chartCaption$: Observable<ChartTypes.CaptionInfo>;
  displayTitle$: Observable<string>;

  encodedChartType$: Observable<string>;

  drilldownEnabled$: Observable<boolean>;
  drillAnywhereEnabled$: Observable<boolean>;
  exploreEnabled$: Observable<boolean>;
  isThemeLoaded$: Observable<boolean>;
  runFailed$: Observable<boolean>;

  constructor(
    private $scope: angular.IScope,
    private $window: angular.IWindowService,
    private $elem: angular.IAugmentedJQuery,
    private rxStore: ReportStore,
    private featureFlagFacade: FeatureFlagsFacade
  ) {
    super();
  }

  showPlaceholder() {
    if (!this.isRendering) {
      this.isRendering = true;
    }
  }

  hidePlaceholder() {
    if (this.isRendering) {
      this.isRendering = false;
    }
  }

  $onInit() {
    this.virtualized = this.$scope.$parent.virtualized; // The parent scope comes from embed-layout
    this.featureFlagFacade
      .asObservable(FeatureFlag.VirtualizedReportDisableDomCache)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((disabled) => (this.disableDomCache = disabled));

    [this.vizType, this.token] = this.id.split('_') as [VizTypes, string];
    this.isThemeLoaded$ = this.rxStore.select(EmbedReportSelectors.isThemeLoaded);
    const chart$ = this.rxStore
      .select(EmbedChartSelectors.createChartSelector(this.token, false))
      .pipe(filter((c) => c.version != null));
    const v1ChartType$ = this.rxStore.select(EmbedChartSelectors.createV1ChartTypeSelector(this.token));
    this.v1View$ = chart$.pipe(map((c) => c.v1View));
    this.isV1$ = chart$.pipe(map((c) => c.version === 1));
    this.vegasRenderInfo$ = this.rxStore.select(EmbedChartSelectors.createVegasRenderInfoSelector(this.token, false));
    this.chartType$ = chart$.pipe(map((c) => c.chartType));
    this.encoding$ = chart$.pipe(map((c) => c.encoding));
    this.format$ = chart$.pipe(map((c) => c.format));
    this.state$ = this.rxStore.select(EmbedChartSelectors.createEmbedStateSelector(this.token));
    this.dataset$ = this.rxStore.select(EmbedChartSelectors.createQueryTokenSelector(this.token)).pipe(
      filter(isPresent),
      switchMap((queryToken) => this.rxStore.select(EmbedReportSelectors.createDatasetSelector(queryToken))),
      shareReplayDefault()
    );
    this.chartReady$ = chart$.pipe(map((chart) => chart.v1View != null || chart.encoding != null));
    this.runFailed$ = this.rxStore.select(EmbedChartSelectors.createQueryFailedSelector(this.token));
    this.isMetricChart$ = this.rxStore.select(EmbedChartSelectors.createIsMetricChartSelector(this.token));

    this.viewer$ = this.rxStore.select(EmbedReportSelectors.viewerType);
    this.isEmbedded$ = this.rxStore.select(EmbedReportSelectors.embedded);
    this.isVegasChart$ = chart$.pipe(
      map((c) => c.version === 2 && c.chartType !== 'table' && c.chartType !== 'bigValue')
    );
    this.isVegasBigValue$ = chart$.pipe(map((c) => c.version === 2 && c.chartType === 'bigValue'));
    this.isVegasTable$ = chart$.pipe(map((c) => c.version === 2 && c.chartType === 'table'));
    this.isPivotTable$ = v1ChartType$.pipe(map((type) => type === 'pivotTable'));
    this.isBigNumber$ = v1ChartType$.pipe(map((type) => type === 'bigNumber'));
    this.isLegacyChart$ = chart$.pipe(
      map((c) => c.version === 1),
      combineLatest(this.isPivotTable$, this.isBigNumber$, (v1, isPivot, isBig) => v1 && !isPivot && !isBig),
      shareReplayDefault()
    );
    this.chartTitle$ = this.rxStore.select(EmbedChartSelectors.createVizTitleSelector(this.token));
    this.chartCaption$ = this.rxStore.select(EmbedChartSelectors.createVizCaptionSelector(this.token));
    this.displayTitle$ = staticCombineLatest([this.chartTitle$, this.chartCaption$]).pipe(
      map(([title, captionInfo]) => {
        return captionInfo?.isTitleHidden ? '' : title;
      })
    );

    this.encodedChartType$ = this.rxStore.select(EmbedChartSelectors.createEncodedChartTypeSelector(this.token));
    this.isInViewport$ = this.rxStore.select(EmbedChartSelectors.createIsInViewportSelector(this.token)).pipe(
      map((inViewport) => !this.virtualized || inViewport),
      shareReplayDefault(),
      takeUntil(this.destroyed$)
    );

    this.isInViewport$
      .pipe(
        takeUntil(this.isV1$.pipe(filter((isLegacy) => isLegacy))), // V1 charts don't support virtualized rendering currently
        takeUntil(this.isVegasBigValue$.pipe(filter((isBV) => isBV))) // Big values don't need vitualized rendering
      )
      .subscribe((inViewport) => {
        if (!inViewport && (!this.disableDomCache || !this.vizRendered)) {
          this.$scope.$evalAsync(() => this.showPlaceholder());
        }

        if (inViewport && !this.renderStartTime) {
          // Set a default render start time in case there's no loading phase or there isn't enough CPU time to fire it
          this.renderStartTime = performance.now();
        }
      });

    this.drillAnywhereEnabled$ = this.rxStore.select(EmbedReportSelectors.isDrillAnywhereEnabled);

    this.rxStore
      .selectNoEmpty(EmbedChartSelectors.createFlamingoStateSelector(this.token))
      .pipe(debounceTime(100), takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.$scope.$evalAsync(() => {
          switch (state) {
            case FlamingoTypes.PlanStatus.Loading:
            case FlamingoTypes.PlanStatus.Init:
              this.showPlaceholder();
              break;
            case FlamingoTypes.PlanStatus.Failed:
              this.hidePlaceholder();
              break;
          }
        });
      });

    this.dataset$
      .pipe(
        takeUntil(this.destroyed$),
        withLatestFrom(this.chartReady$),
        takeUntil(this.isV1$.pipe(filter((legacy) => legacy)))
      )
      .subscribe(([ds, isReady]) => {
        if (ds == null) {
          // if the chart is orphaned, we don't want to show the placeholder on report view pages
          if (isReady) {
            this.$scope.$evalAsync(() => {
              this.showPlaceholder();
            });
          }
        } else if (ds.state === 'failed') {
          this.$scope.$evalAsync(() => {
            this.hidePlaceholder();
          });
        }
      });

    // render end callback on legacy charts is too unreliable, so just show when data is available
    this.isV1$
      .pipe(
        filter((isLegacy) => isLegacy),
        switchMap(() => this.dataset$),
        takeUntil(this.destroyed$)
      )
      .subscribe((ds) => {
        if (!this.renderStartTime) {
          // Mark a start time for legacy charts
          this.renderStartTime = performance.now();
        }

        if (ds == null) {
          this.$scope.$evalAsync(() => this.showPlaceholder());
        } else {
          this.$scope.$evalAsync(() => this.hidePlaceholder());
        }
      });

    this.exploreEnabled$ = staticCombineLatest([
      this.rxStore.select(EmbedReportSelectors.exploreEnabled),
      this.isMetricChart$,
    ]).pipe(map(([isEnabled, isMetricChart]) => isEnabled && !isMetricChart));

    this.drilldownEnabled$ = staticCombineLatest([
      this.rxStore.select(EmbedReportSelectors.drilldownEnabled),
      this.vegasRenderInfo$,
      this.isMetricChart$,
    ]).pipe(
      map(([isEnabled, view, isMetric]) => isEnabled && view?.format && !isMetric && view.format.drilldownEnabled)
    );
  }

  onAction(action: EmbedActions) {
    this.rxStore.dispatch(action);
  }

  renderStart() {
    this.renderStartTime = performance.now();
    this.showPlaceholder();
  }

  renderEnd(visualizationRendered = true, type, options = {}) {
    // visualizationRendered: Refers to visualiztion with graph data represented
    // e.g. pie chart, line graph, VE, etc. with graph appearing
    // as opposed to successfully rendered visualization w/o graph appearing
    // e.g. chart has error, invalid or incomplete configuration would call renderEnd but visualization would not be present
    this.hidePlaceholder();
    this.vizRendered = visualizationRendered;

    this.rxStore.dispatch({
      type: EmbedActionType.ChartRendered,
      token: this.token,
      visualizationRendered: visualizationRendered,
      duration: performance.now() - this.renderStartTime,
      chartType: type,
      timestamp: performance.now() + performance.timeOrigin,
      options,
    });
  }
}

export const ModeChartComponent: angular.IComponentOptions = {
  bindings: {
    id: '@',
  },
  controller: ModeChartController,
  controllerAs: 'ctrl',
  template: `
  <div class="chart-container-component"
       tabindex="0"
       ng-class="['viz-' + (ctrl.chartType$ | async:this), { 'legacy': (ctrl.isV1$ | async:this), 'is-rendering': ctrl.isRendering }]"
  >
    <viz-placeholder
      ng-if="ctrl.isRendering"
      is-loading="true"
      viz-title="ctrl.displayTitle$ | async:this"
      viz-type="ctrl.vizType"
      chart-type="ctrl.encodedChartType$ | async:this"
    ></viz-placeholder>
    <run-failed-error
      ng-if="ctrl.runFailed$ | async:this"
      on-select-query="ctrl.selectQuery()"
      chart-type="ctrl.chartType$ | async:this"
    ></run-failed-error>
    <div ng-if="!(ctrl.runFailed$ | async:this)">
      <vegas-chart-too
        viz-resizer
        ng-if="::(ctrl.isVegasChart$ | async:this)"
        id="{{ ::ctrl.id }}"
        render-info="ctrl.vegasRenderInfo$ | async:this"
        on-action="ctrl.onAction($action)"
        drilldown-enabled="ctrl.drilldownEnabled$ | async:this"
        drill-anywhere-enabled="ctrl.drillAnywhereEnabled$ | async:this"
        viewer="::ctrl.viewer$ | async:this"
        in-viewport="ctrl.isInViewport$ | async:this"
        is-embedded="::ctrl.isEmbedded$ | async:this"
        is-metric-chart="::ctrl.isMetricChart$ | async:this"
        explore-enabled="ctrl.exploreEnabled$ | async:this"
        on-render-start="ctrl.renderStart()"
        on-render-end="ctrl.renderEnd($visualizationRendered, $type, $options)"
        disable-dom-cache="ctrl.disableDomCache"
      ></vegas-chart-too>
      <vegas-big-value
        viz-resizer
        ng-if="::(ctrl.isVegasBigValue$ | async:this)"
        id="{{ ::ctrl.id }}"
        render-info="ctrl.vegasRenderInfo$ | async:this"
        on-action="ctrl.onAction($action)"
        viewer="::ctrl.viewer$ | async:this"
        in-viewport="ctrl.isInViewport$ | async:this"
        is-embedded="::ctrl.isEmbedded$ | async:this"
        explore-enabled="ctrl.exploreEnabled$ | async:this"
        on-render-start="ctrl.renderStart()"
        on-render-end="ctrl.renderEnd($visualizationRendered, 'bigValue')"
      ></vegas-big-value>
      <vegas-table
        viz-resizer
        ng-if="::ctrl.isVegasTable$ | async:this"
        id="{{ ::ctrl.id }}"
        render-info="ctrl.vegasRenderInfo$ | async:this"
        theme-loaded="ctrl.isThemeLoaded$ | async:this"
        is-virtualized="ctrl.virtualized"
        viewer="::ctrl.viewer$ | async:this"
        in-viewport="ctrl.isInViewport$ | async:this"
        is-metric-chart="::ctrl.isMetricChart$ | async:this"
        explore-enabled="ctrl.exploreEnabled$ | async:this"
        on-title-changed="ctrl.updateTitle($title)"
        on-render-start="ctrl.renderStart()"
        on-render-end="ctrl.renderEnd($visualizationRendered, 'vegasTable')"
        on-records-loaded="ctrl.hidePlaceholder()"
        on-action="ctrl.onAction($action)"
        disable-dom-cache="ctrl.disableDomCache"
      ></vegas-table>
      <mode-pivot-table
        view="ctrl.v1View$ | async:this"
        ng-if="(ctrl.isPivotTable$ | async:this) && (ctrl.dataset$ | async:this)"
        dataset="ctrl.dataset$ | async:this"
        id="{{ ::ctrl.id }}"
        on-action="ctrl.onAction($action)"
        viewer="::ctrl.viewer$ | async:this"
        on-render-start="ctrl.renderStart()"
        on-render-end="ctrl.renderEnd($visualizationRendered, 'pivotTable')"
        is-chart-visible="true"
      ></mode-pivot-table>
      <mode-big-number
        ng-if="(ctrl.isBigNumber$ | async:this) && (ctrl.dataset$ | async:this)"
        set-view="ctrl.v1View$ | async:this"
        dataset="ctrl.dataset$ | async:this"
        id="{{ ::ctrl.id }}"
        on-action="ctrl.onAction($action)"
        viewer="::ctrl.viewer$ | async:this"
        on-render-start="ctrl.renderStart()"
        on-render-end="ctrl.renderEnd($visualizationRendered, 'bigNumber')"
      ></mode-big-number>
      <mode-multi-chart
        id="{{ ::ctrl.id }}"
        dataset="ctrl.dataset$ | async:this"
        set-view="ctrl.v1View$ | async:this"
        ng-if="(ctrl.isLegacyChart$ | async:this) && (ctrl.dataset$ | async:this)"
        viewer="::ctrl.viewer$ | async:this"
        on-action="ctrl.onAction($action)"
        on-render-end="ctrl.renderEnd($visualizationRendered, 'legacyChart')"
        redraw-on-data-change="true"
      ></mode-multi-chart>
    </div>
  </div>
  `,
};
